<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { extensionDownloadContext } from '~/utils/extension-handoff.mjs';
import { trackEvent } from '~/utils/analytics';

const route = useRoute();
const { locale } = useI18n();
const zh = computed(() => locale.value.startsWith('zh'));
const { isAuthenticated, userAccount, handleSignIn } = useAuthState();
const context = computed(() => extensionDownloadContext(route.query));
const signedIn = computed(() => isAuthenticated.value && !!userAccount.value?.token);
const loggingIn = ref(false);
const copy = (en: string, cn: string) => zh.value ? cn : en;
const attribution = { utm_source: 'musicmint_extension', utm_medium: 'extension', utm_campaign: 'login' };
const toolLink = (path: string) => ({ path, query: attribution, hash: path === '/vocal-separation' && context.value.videoId ? `#mm_video_id=${context.value.videoId}` : '' });
async function login() {
  loggingIn.value = true;
  try { await handleSignIn(); } finally { loggingIn.value = false; }
}
onMounted(() => trackEvent('extension_landing_view', { action: 'continue_download', format: context.value.type === 2 ? 'mp4' : 'mp3' }));
watch(signedIn, ready => { if (ready) trackEvent('extension_account_ready', { action: 'continue_download' }); });
useSeoMeta({ title: 'Continue with MusicMint', robots: 'noindex, nofollow' });
</script>

<template>
  <main class="extension-landing">
    <p class="extension-kicker">MUSICMINT · BROWSER TOOLS</p>
    <h1>{{ signedIn ? copy('You’re ready to continue.', '已登录，可以继续了。') : copy('Continue your download.', '继续刚才的下载。') }}</h1>
    <p class="extension-description">{{ copy('Your video stays selected. Sign in, then return to the extension to confirm the download.', '视频已保留。登录后返回插件，确认并继续下载。') }}</p>
    <div v-if="context.videoId" class="selected-video">
      <img :src="`https://img.youtube.com/vi/${context.videoId}/mqdefault.jpg`" alt="" width="160" height="90">
      <div><strong>YouTube · {{ context.videoId }}</strong><p>{{ context.type === 2 ? 'MP4' : 'MP3' }}</p></div>
    </div>
    <button v-if="!signedIn" class="extension-primary" :disabled="loggingIn" @click="login">{{ copy('Sign in to MusicMint', '登录 MusicMint') }}</button>
    <a v-else-if="context.videoUrl" :href="context.videoUrl" class="extension-primary" @click="trackEvent('extension_return_to_video', { action: 'continue_download' })">{{ copy('Return to YouTube', '回到 YouTube 继续下载') }}</a>
    <p v-else class="extension-success">{{ copy('Open the MusicMint extension to continue.', '打开 MusicMint 插件继续操作。') }}</p>
    <p class="extension-note">{{ copy('Downloads use your account’s current credit rules. Confirm in the extension before submitting. Download only content you own or are authorized to use.', '下载按账户当前积分规则计费，返回插件后确认提交。请仅下载你拥有或获准使用的内容。') }}</p>
    <section class="extension-next"><h2>{{ copy('More for your music.', '音频到手，还能做什么？') }}</h2><NuxtLink :to="toolLink('/vocal-separation')">{{ copy('Separate vocals and instrumental', '提取人声与伴奏') }} ↗</NuxtLink><NuxtLink :to="toolLink('/create')">{{ copy('Create original music for your videos', '为你的视频生成原创配乐') }} ↗</NuxtLink></section>
  </main>
</template>

<style scoped>
.extension-landing { max-width:700px; margin:40px auto; padding:32px 24px; color:var(--mint-ink); }
.extension-kicker { font-size:12px; letter-spacing:2px; color:var(--mint-muted); margin-bottom:18px; }
h1 { font-size:clamp(28px,5vw,42px); font-weight:650; line-height:1.2; margin-bottom:16px; }
.extension-description { color:var(--mint-muted); font-size:16px; line-height:1.7; max-width:560px; }
.selected-video { display:flex; align-items:center; gap:18px; padding:18px; border:1px solid var(--mint-border); border-radius:14px; background:var(--mint-surface); margin:26px 0; }
.selected-video img { border-radius:8px; object-fit:cover; max-width:40%; height:auto; }
.selected-video strong { overflow-wrap:anywhere; }
.selected-video p { color:var(--mint-muted); margin-top:6px; }
.extension-primary { display:inline-block; background:#a7f3d0; color:#173c32; border:0; border-radius:10px; padding:13px 24px; font-weight:650; cursor:pointer; margin:12px 0; }
.extension-primary:disabled { opacity:.5; cursor:wait; }
.extension-primary:focus-visible, a:focus-visible { outline:3px solid #34d399; outline-offset:4px; }
.extension-note { color:var(--mint-muted); font-size:12px; line-height:1.7; max-width:550px; }
.extension-next { margin-top:32px; border-top:1px solid var(--mint-border); padding-top:24px; }
.extension-next h2 { font-size:18px; margin-bottom:14px; }
.extension-next a { display:block; padding:12px 0; color:var(--mint-ink); text-decoration:underline; text-underline-offset:4px; }
.extension-success { margin:24px 0; }
</style>
